import { formatNumber } from "./format.ts";
import { ChartFrame, type ChartTone, fillClass } from "./frame.tsx";

export interface BarDatum {
  /** A category name, or a day (`YYYY-MM-DD`) formatted by `formatLabel`. */
  label: string;
  value: number;
}

export interface BarChartProps {
  title: string;
  /** One sentence giving the takeaway; the chart's accessible description. */
  summary: string;
  data: readonly BarDatum[];
  /**
   * `horizontal`: one labelled row per category with its value written at the bar's end (top
   * projects, breakdowns). `vertical`: a compact series over time (days).
   */
  orientation?: "horizontal" | "vertical";
  tone?: ChartTone;
  formatValue?: (value: number) => string;
  formatLabel?: (label: string) => string;
  /** Table header of the label column, e.g. "Day" or "Project". */
  labelHeader: string;
  /** Table header of the value column, e.g. "Questions". */
  valueHeader: string;
  className?: string;
}

const identity = (label: string) => label;

/** One series of bars. A single series needs no colour key: position and labels carry it. */
export function BarChart({
  title,
  summary,
  data,
  orientation = "horizontal",
  tone = 1,
  formatValue = formatNumber,
  formatLabel = identity,
  labelHeader,
  valueHeader,
  className,
}: BarChartProps) {
  const max = Math.max(0, ...data.map((d) => d.value));
  const table = {
    columns: [labelHeader, valueHeader],
    rows: data.map((d) => [formatLabel(d.label), formatValue(d.value)]),
  };
  return (
    <ChartFrame title={title} summary={summary} table={table} className={className}>
      {({ titleId, summaryId }) =>
        orientation === "horizontal" ? (
          <ul
            aria-labelledby={titleId}
            aria-describedby={summaryId}
            className="flex flex-col gap-1.5"
          >
            {data.map((d) => (
              <li
                key={d.label}
                className="grid grid-cols-[minmax(0,10rem)_1fr_auto] items-center gap-2 text-sm"
              >
                <span className="truncate text-chart-label" title={formatLabel(d.label)}>
                  {formatLabel(d.label)}
                </span>
                <svg
                  viewBox="0 0 100 10"
                  preserveAspectRatio="none"
                  className="h-3 w-full"
                  aria-hidden="true"
                >
                  <rect
                    x={0}
                    y={0}
                    height={10}
                    width={max > 0 ? Math.max((d.value / max) * 100, d.value > 0 ? 0.5 : 0) : 0}
                    rx={1}
                    className={fillClass(tone)}
                  />
                </svg>
                <span className="text-right tabular-nums text-fg">{formatValue(d.value)}</span>
              </li>
            ))}
          </ul>
        ) : (
          <VerticalBars
            data={data}
            max={max}
            tone={tone}
            titleText={title}
            summary={summaryId}
            formatValue={formatValue}
            formatLabel={formatLabel}
          />
        )
      }
    </ChartFrame>
  );
}

function VerticalBars({
  data,
  max,
  tone,
  titleText,
  summary,
  formatValue,
  formatLabel,
}: {
  data: readonly BarDatum[];
  max: number;
  tone: ChartTone;
  titleText: string;
  summary: string;
  formatValue: (value: number) => string;
  formatLabel: (label: string) => string;
}) {
  const step = 10;
  const width = Math.max(data.length, 1) * step;
  const first = data[0];
  const last = data.at(-1);
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-stretch gap-2">
        <div className="flex flex-col justify-between text-right text-xs tabular-nums text-chart-label">
          <span>{formatValue(max)}</span>
          <span>{formatValue(0)}</span>
        </div>
        <svg
          role="img"
          aria-describedby={summary}
          viewBox={`0 0 ${width} 100`}
          preserveAspectRatio="none"
          className="h-32 w-full min-w-0 border-b border-chart-grid"
        >
          <title>{titleText}</title>
          {data.map((d, i) => {
            const height = max > 0 ? (d.value / max) * 100 : 0;
            return (
              <rect
                key={d.label}
                x={i * step + 1.5}
                y={100 - height}
                width={step - 3}
                height={height}
                className={fillClass(tone)}
              >
                <title>{`${formatLabel(d.label)}: ${formatValue(d.value)}`}</title>
              </rect>
            );
          })}
        </svg>
      </div>
      {first && last ? (
        <div className="flex justify-between pl-8 text-xs text-chart-label" aria-hidden="true">
          <span>{formatLabel(first.label)}</span>
          {last !== first ? <span>{formatLabel(last.label)}</span> : null}
        </div>
      ) : null}
    </div>
  );
}
